(() => {
    const CONFIG = window.FramewerkSearchConfig || {};
    const BRAIN = window.FramewerkSearchBrain || {};
    const RANKER = window.FramewerkSearchRanker || {};
    const STORAGE_KEY = CONFIG.storageKey || 'framewerk-search-recent-v2';
    const VALIDATION_STORAGE_KEY = CONFIG.validationStorageKey || 'framewerk-search-valid-presets-v2';
    const CACHE_TTL = CONFIG.cacheTtl || 5 * 60 * 1000;
    const VALIDATION_TTL = CONFIG.validationTtl || 6 * 60 * 60 * 1000;
    const REQUEST_TIMEOUT = CONFIG.requestTimeout || 8000;
    const MAX_RECENT = CONFIG.maxRecent || 6;

    const modelCodes = CONFIG.modelCodes || ['VB','VC','VH','VK','VL','VN','VP','VQ','VR','VS','VT','VX','VY','VZ','VE','VF'];

    const aliases = BRAIN.aliases || [];

    const intentWords = new Set([
      'commodore', 'holden', 'parts', 'part', 'for', 'the', 'a', 'an', 'and', 'or', 'to', 'of',
      'new', 'replacement', 'front', 'rear', 'left', 'right', 'pair', 'kit'
    ]);

    const escapeHTML = (value = '') => String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    const cleanQuery = (value) => {
      let query = String(value || '')
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[^a-zA-Z0-9'&+./\-\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      aliases.forEach(([pattern, replacement]) => {
        query = query.replace(pattern, replacement);
      });

      const modelOnly = query.match(new RegExp(`^(${modelCodes.join('|')})$`, 'i'));
      if (modelOnly) return `${modelOnly[1].toUpperCase()} Commodore`;

      const startsWithModel = query.match(new RegExp(`^(${modelCodes.join('|')})\\b(?!\\s+Commodore)`, 'i'));
      if (startsWithModel) {
        query = query.replace(startsWithModel[0], `${startsWithModel[1].toUpperCase()} Commodore`);
      }

      return query;
    };

    const getTerms = (query) => cleanQuery(query)
      .toLowerCase()
      .split(/\s+/)
      .filter((term) => term.length > 1 && !intentWords.has(term));

    const highlight = (text, query) => {
      let output = escapeHTML(text);
      const terms = [...new Set(getTerms(query))].sort((a, b) => b.length - a.length);
      terms.forEach((term) => {
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        output = output.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>');
      });
      return output;
    };

    const getRecent = () => {
      try {
        const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(value) ? value.slice(0, MAX_RECENT) : [];
      } catch (_) {
        return [];
      }
    };

    const saveRecent = (query) => {
      const cleaned = cleanQuery(query);
      if (cleaned.length < 2) return;
      const next = [cleaned, ...getRecent().filter((item) => item.toLowerCase() !== cleaned.toLowerCase())].slice(0, MAX_RECENT);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (_) {}
    };

    const removeRecent = (query) => {
      const cleaned = cleanQuery(query).toLowerCase();
      if (!cleaned) return;
      const next = getRecent().filter((item) => cleanQuery(item).toLowerCase() !== cleaned);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (_) {}
    };

    const formatPrice = (value) => {
      if (value === null || value === undefined || value === '') return '';
      if (typeof value === 'string' && /[$£€¥]/.test(value)) return escapeHTML(value);
      const number = Number(value);
      if (!Number.isFinite(number)) return escapeHTML(value);
      const currency = window.Shopify?.currency?.active || 'AUD';
      try {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(number);
      } catch (_) {
        return `$${number.toFixed(2)}`;
      }
    };

    const productImage = (product) => {
      const image = product.featured_image || product.image || product.featured_media;
      if (!image) return '';
      if (typeof image === 'string') return image;
      return image.url || image.src || '';
    };

    const productSkuMatch = (product, query) => {
      const normalized = cleanQuery(query).toLowerCase().replace(/\s+/g, '');
      const variants = Array.isArray(product.variants) ? product.variants : [];
      return variants.some((variant) => String(variant.sku || '').toLowerCase().replace(/\s+/g, '') === normalized);
    };

    const productScore = (product, query, interpretation = null) => {
      if (typeof RANKER.score === 'function') return RANKER.score(product, query, interpretation);
      const title = String(product.title || '').toLowerCase();
      return title.includes(String(query || '').toLowerCase()) ? 100 : 0;
    };

    const getSuggestions = (query) => {
      if (typeof BRAIN.suggest === 'function') {
        const smart = BRAIN.suggest(query, 4);
        if (smart.length) return smart;
      }
      const q = cleanQuery(query);
      const model = q.match(new RegExp(`\\b(${modelCodes.join('|')})\\b`, 'i'))?.[1]?.toUpperCase();
      const suggestions = [];
      if (model) {
        ['headlights', 'tail lights', 'interior', 'door handles'].forEach((part) => suggestions.push(`${model} Commodore ${part}`));
      } else {
        suggestions.push('VL Commodore parts', 'VE Commodore parts', 'VF Commodore parts', 'Holden radios');
      }
      return suggestions.filter((item) => item.toLowerCase() !== q.toLowerCase()).slice(0, 4);
    };

    const track = (eventName, detail = {}) => {
      const eventDetail = { eventName, ...detail, timestamp: new Date().toISOString() };
      window.dispatchEvent(new CustomEvent('framewerk:search-analytics', { detail: eventDetail }));
      try {
        const key = 'framewerk-search-analytics-v2';
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        existing.push(eventDetail);
        localStorage.setItem(key, JSON.stringify(existing.slice(-500)));
      } catch (_) {}
    };

    class FramewerkAISearch {
      constructor(root) {
        this.root = root;
        this.dialog = root.closest('dialog');
        this.form = root.querySelector('[data-ai-search-form]');
        this.input = root.querySelector('[data-ai-search-input]');
        this.clearButton = root.querySelector('[data-ai-search-clear]');
        this.main = root.querySelector('[data-ai-search-main]');
        this.sidebar = root.querySelector('[data-ai-search-sidebar]');
        this.welcome = root.querySelector('[data-ai-search-welcome]');
        this.results = root.querySelector('[data-ai-results]');
        this.footer = root.querySelector('[data-ai-search-footer]');
        this.viewAll = root.querySelector('[data-ai-view-all]');
        this.interpretation = root.querySelector('[data-ai-interpretation]');
        this.recentSection = root.querySelector('[data-ai-recent-section]');
        this.recentList = root.querySelector('[data-ai-recent-list]');
        this.clearRecent = root.querySelector('[data-ai-clear-recent]');
        this.mobileRecentSection = root.querySelector('[data-ai-mobile-recent-section]');
        this.mobileRecentList = root.querySelector('[data-ai-mobile-recent-list]');
        this.mobileClearRecent = root.querySelector('[data-ai-mobile-clear-recent]');
        this.trendingSection = root.querySelector('[data-ai-trending-section]');
        this.trendingButtons = [...root.querySelectorAll('[data-ai-validate-query]')];
        this.presetSections = [...root.querySelectorAll('[data-ai-preset-section]')];
        this.validatedQueries = new Set();
        this.presetsValidated = false;
        this.endpoint = root.dataset.endpoint;
        this.searchUrl = root.dataset.searchUrl;
        this.cache = new Map();
        this.abortController = null;
        this.debounceTimer = null;
        this.activeIndex = -1;
        this.currentQuery = '';
        this.lastInterpretation = null;
        this.bind();
        this.renderRecent();
      }

      bind() {
        this.input.addEventListener('input', () => this.onInput());
        this.input.addEventListener('keydown', (event) => this.onKeyDown(event));
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.clearButton.addEventListener('click', () => this.reset());
        const clearRecentSearches = () => {
          try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
          this.renderRecent();
          this.input.focus({ preventScroll: true });
        };
        this.clearRecent?.addEventListener('click', clearRecentSearches);
        this.mobileClearRecent?.addEventListener('click', clearRecentSearches);
        this.viewAll.addEventListener('click', () => this.submitCurrent());

        this.root.addEventListener('click', (event) => {
          const queryButton = event.target.closest('[data-ai-query]');
          if (queryButton) {
            event.preventDefault();
            const selectedQuery = queryButton.dataset.aiQuery || queryButton.textContent || '';
            this.setQuery(selectedQuery);
            return;
          }

          const resultLink = event.target.closest('[data-ai-product-link], .framewerk-ai-search__secondary-link');
          if (resultLink) {
            saveRecent(this.currentQuery);
            track('search_product_click', { originalQuery: this.lastInterpretation?.original || this.currentQuery, interpretedQuery: this.currentQuery, url: resultLink.href || '', source: 'modal' });
            this.renderRecent();
          }
        });

        if (this.dialog) {
          new MutationObserver(() => {
            if (this.dialog.open) {
              this.renderRecent();
              this.validateTrendingSearches();
              requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
            }
          }).observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
        }
      }

      renderRecent() {
        const recent = getRecent();
        this.recentList?.replaceChildren();
        this.mobileRecentList?.replaceChildren();
        if (this.recentSection) this.recentSection.hidden = recent.length === 0;
        if (this.mobileRecentSection) this.mobileRecentSection.hidden = recent.length === 0;

        recent.forEach((query) => {
          const desktopButton = document.createElement('button');
          desktopButton.type = 'button';
          desktopButton.className = 'framewerk-ai-search__recent-link';
          desktopButton.dataset.aiQuery = query;
          desktopButton.textContent = query;
          this.recentList?.appendChild(desktopButton);

          const mobileButton = document.createElement('button');
          mobileButton.type = 'button';
          mobileButton.dataset.aiQuery = query;
          mobileButton.textContent = query;
          this.mobileRecentList?.appendChild(mobileButton);
        });
      }


      readValidationCache() {
        try {
          const cached = JSON.parse(sessionStorage.getItem(VALIDATION_STORAGE_KEY) || '{}');
          if (!cached || Date.now() - Number(cached.time || 0) >= VALIDATION_TTL || !Array.isArray(cached.valid)) {
            return null;
          }
          return new Set(cached.valid.map((query) => String(query).toLowerCase()));
        } catch (_) {
          return null;
        }
      }

      writeValidationCache(validQueries) {
        try {
          sessionStorage.setItem(VALIDATION_STORAGE_KEY, JSON.stringify({
            time: Date.now(),
            valid: [...validQueries]
          }));
        } catch (_) {}
      }

      applyValidatedPresets(validQueries) {
        this.validatedQueries = new Set(validQueries);

        this.trendingButtons.forEach((button) => {
          const query = cleanQuery(button.dataset.aiQuery || button.textContent || '').toLowerCase();
          if (!query || !this.validatedQueries.has(query)) button.remove();
        });

        this.presetSections.forEach((section) => {
          const remaining = section.querySelectorAll('[data-ai-validate-query]').length;
          section.hidden = remaining === 0;
        });
      }

      async validateTrendingSearches() {
        if (this.presetsValidated || !this.trendingButtons.length) return;
        this.presetsValidated = true;

        const cachedValid = this.readValidationCache();
        if (cachedValid) {
          this.applyValidatedPresets(cachedValid);
          return;
        }

        const buttonGroups = new Map();
        this.trendingButtons.forEach((button) => {
          const query = cleanQuery(button.dataset.aiQuery || button.textContent || '');
          if (!query) {
            button.remove();
            return;
          }
          const key = query.toLowerCase();
          if (!buttonGroups.has(key)) buttonGroups.set(key, query);
        });

        const validQueries = new Set();
        const checks = [...buttonGroups.entries()].map(async ([key, query]) => {
          const params = new URLSearchParams({
            q: query,
            'resources[type]': 'product',
            'resources[limit]': '1',
            'resources[options][unavailable_products]': 'last',
            'resources[options][fields]': 'title,product_type,variants.title,variants.sku,tag'
          });

          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

          try {
            const response = await fetch(`${this.endpoint}?${params.toString()}`, {
              headers: { Accept: 'application/json' },
              signal: controller.signal
            });
            if (!response.ok) return;
            const data = await response.json();
            const resources = this.getResources(data);
            if (Array.isArray(resources.products) && resources.products.length > 0) validQueries.add(key);
          } catch (_) {
            // Fail closed: an unverified shortcut is not promoted.
          } finally {
            clearTimeout(timeout);
          }
        });

        await Promise.allSettled(checks);
        this.writeValidationCache(validQueries);
        this.applyValidatedPresets(validQueries);
      }

      enterResultsMode() {
        this.root.classList.add('has-query');
        this.welcome.hidden = true;
        this.results.hidden = false;
        this.main.scrollTop = 0;
      }

      onInput() {
        const raw = this.input.value;
        this.clearButton.hidden = raw.length === 0;
        clearTimeout(this.debounceTimer);
        if (!raw.trim()) {
          this.reset(false);
          return;
        }
        this.enterResultsMode();
        this.debounceTimer = setTimeout(() => this.search(raw), 210);
      }

      onSubmit(event) {
        const query = cleanQuery(this.input.value);
        if (!query || !this.searchUrl) return;
        event.preventDefault();
        this.submitCurrent();
      }

      submitCurrent() {
        const originalQuery = cleanQuery(this.input.value);
        if (!originalQuery) return;

        const plan = this.lastInterpretation && cleanQuery(this.lastInterpretation.original) === originalQuery
          ? this.lastInterpretation
          : (typeof BRAIN.interpret === 'function' ? BRAIN.interpret(originalQuery) : null);

        const searchQuery = cleanQuery(plan?.primary || originalQuery);

        saveRecent(originalQuery);
        this.renderRecent();

        if (this.searchUrl) {
          const params = new URLSearchParams({
            q: searchQuery,
            type: 'product',
            'options[prefix]': 'last',
            hc_smart: '1',
            hc_original: originalQuery
          });

          if (plan?.intentId) params.set('hc_intent', plan.intentId);
          track('search_submit', { originalQuery, interpretedQuery: searchQuery, intentId: plan?.intentId || null, confidence: plan?.confidence || null, source: 'modal' });
          window.location.assign(`${this.searchUrl}?${params.toString()}`);
          return;
        }

        this.form.submit();
      }

      setQuery(query) {
        const cleaned = cleanQuery(query);
        this.input.value = cleaned;
        this.clearButton.hidden = false;
        this.enterResultsMode();
        this.input.focus({ preventScroll: true });
        this.search(cleaned);
      }

      reset(focus = true) {
        clearTimeout(this.debounceTimer);
        this.abortController?.abort();
        this.currentQuery = '';
        this.lastInterpretation = null;
        this.input.value = '';
        this.input.setAttribute('aria-expanded', 'false');
        this.clearButton.hidden = true;
        this.root.classList.remove('is-loading', 'has-query');
        this.main.setAttribute('aria-busy', 'false');
        this.results.hidden = true;
        this.results.innerHTML = '';
        this.welcome.hidden = false;
        this.footer.hidden = true;
        this.activeIndex = -1;
        if (focus) this.input.focus({ preventScroll: true });
      }
      async fetchSearchData(query, signal) {
        const params = new URLSearchParams({
          q: query,
          'resources[type]': 'product',
          'resources[limit]': String(CONFIG.maxPredictiveResults || 12),
          'resources[options][unavailable_products]': 'last',
          'resources[options][fields]': 'title,product_type,variants.title,variants.sku,tag,body'
        });

        const response = await fetch(`${this.endpoint}?${params.toString()}`, {
          headers: { Accept: 'application/json' },
          signal
        });
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
        return response.json();
      }

      mergeSearchData(dataSets) {
        const merged = { resources: { results: { products: [], collections: [], pages: [], articles: [] } } };
        const seen = { products: new Set(), collections: new Set(), pages: new Set(), articles: new Set() };
        const keyFor = (item) => String(item?.id || item?.url || item?.handle || item?.title || '');

        dataSets.forEach((data) => {
          const resources = this.getResources(data);
          ['products', 'collections', 'pages', 'articles'].forEach((type) => {
            (resources[type] || []).forEach((item) => {
              const key = keyFor(item);
              if (!key || seen[type].has(key)) return;
              seen[type].add(key);
              merged.resources.results[type].push(item);
            });
          });
        });

        merged.resources.results.products = merged.resources.results.products.slice(0, CONFIG.maxMergedProducts || 18);
        return merged;
      }

      async search(rawQuery) {
        const entered = cleanQuery(rawQuery);
        if (!entered) return this.reset(false);

        const plan = typeof BRAIN.interpret === 'function'
          ? BRAIN.interpret(entered)
          : { original: entered, primary: entered, queries: [entered], fallbackQueries: [], label: entered };

        const query = cleanQuery(plan.primary || entered);
        this.lastInterpretation = plan;
        this.currentQuery = query;

        this.enterResultsMode();
        this.root.classList.add('is-loading');
        this.main.setAttribute('aria-busy', 'true');
        this.input.setAttribute('aria-expanded', 'true');
        this.footer.hidden = false;
        this.interpretation.innerHTML = plan.explanation
          ? escapeHTML(plan.explanation)
          : `Searching for <strong>${escapeHTML(entered)}</strong>`;

        this.abortController?.abort();
        this.abortController = new AbortController();
        const requestController = this.abortController;
        const requestQuery = query;
        const requestTimeout = setTimeout(() => requestController.abort(), REQUEST_TIMEOUT);

        try {
          // Keep the modal and full search page on the exact same interpreted query.
          // Do not merge expansions or fallback queries into the modal preview, because
          // the Shopify /search page can only execute one query at a time.
          const data = await this.fetchSearchData(query, requestController.signal);

          if (requestQuery === this.currentQuery) this.render(data, query);
        } catch (error) {
          if (error.name !== 'AbortError' && requestQuery === this.currentQuery) this.renderError(entered);
        } finally {
          clearTimeout(requestTimeout);
        }
      }

      getResources(data) {
        return data?.resources?.results || data?.resources || data?.results || {};
      }

      render(data, query) {
        const resources = this.getResources(data);
        // Preserve Shopify's returned order so the modal preview matches the
        // leading products on the full search results page as closely as possible.
        const products = [...(resources.products || [])];
        const collections = resources.collections || [];
        const pages = resources.pages || [];
        const articles = resources.articles || [];
        const total = products.length + collections.length + pages.length + articles.length;

        track(total ? 'search_results' : 'search_no_results', {
          originalQuery: this.lastInterpretation?.original || query,
          interpretedQuery: query,
          intentId: this.lastInterpretation?.intentId || null,
          confidence: this.lastInterpretation?.confidence || null,
          resultCount: total,
          source: 'modal'
        });

        this.root.classList.remove('is-loading');
        this.main.setAttribute('aria-busy', 'false');
        this.interpretation.innerHTML = this.lastInterpretation?.explanation
          ? escapeHTML(this.lastInterpretation.explanation)
          : `Smart search understood: <strong>${escapeHTML(query)}</strong>`;
        this.viewAll.textContent = total ? `View all results for “${query}”` : `Search all products for “${query}”`;

        if (!total) {
          removeRecent(query);
          this.renderRecent();
          this.renderEmpty(query);
          return;
        }

        const productMarkup = products.length ? `
          <div class="framewerk-ai-search__results-heading">
            <h3 class="framewerk-ai-search__results-title">Best matches</h3>
            <span class="framewerk-ai-search__count">${products.length} product${products.length === 1 ? '' : 's'} shown</span>
          </div>
          <div class="framewerk-ai-search__product-grid">
            ${products.map((product) => this.productCard(product, query)).join('')}
          </div>
        ` : '';

        const secondaryItems = [
          ...collections.map((item) => ({ ...item, type: 'Collection' })),
          ...pages.map((item) => ({ ...item, type: 'Page' })),
          ...articles.map((item) => ({ ...item, type: 'Article' }))
        ].slice(0, 8);

        const secondaryMarkup = secondaryItems.length ? `
          <section class="framewerk-ai-search__secondary">
            <h4 class="framewerk-ai-search__secondary-title">Related matches</h4>
            <div class="framewerk-ai-search__secondary-links">
              ${secondaryItems.map((item) => `
                <a class="framewerk-ai-search__secondary-link" href="${escapeHTML(item.url || '#')}">
                  ${escapeHTML(item.title || '')} · ${item.type}
                </a>
              `).join('')}
            </div>
          </section>
        ` : '';

        this.results.innerHTML = productMarkup + secondaryMarkup;
        this.activeIndex = -1;
      }

      productCard(product, query) {
        const image = productImage(product);
        const exactSku = productSkuMatch(product, query);
        const type = product.product_type || product.vendor || '';
        const available = product.available !== false;
        const price = formatPrice(product.price_min ?? product.price);
        const url = product.url || `/products/${product.handle || ''}`;

        return `
          <a class="framewerk-ai-search__product" href="${escapeHTML(url)}" data-ai-product-link>
            <div class="framewerk-ai-search__product-image">
              ${image ? `<img src="${escapeHTML(image)}" alt="" loading="lazy" width="220" height="220">` : ''}
            </div>
            <div class="framewerk-ai-search__product-copy">
              <h4 class="framewerk-ai-search__product-title">${highlight(product.title || '', query)}</h4>
              <div class="framewerk-ai-search__product-meta">
                ${exactSku ? '<span class="framewerk-ai-search__badge framewerk-ai-search__badge--exact">Exact SKU match</span>' : ''}
                ${type ? `<span class="framewerk-ai-search__badge">${escapeHTML(type)}</span>` : ''}
              </div>
              <div class="framewerk-ai-search__product-bottom">
                <span class="framewerk-ai-search__price">${price}</span>
                <span class="framewerk-ai-search__availability${available ? '' : ' is-unavailable'}">${available ? 'Available' : 'Sold out'}</span>
              </div>
            </div>
          </a>
        `;
      }

      renderEmpty(query) {
        const brainFallbacks = getSuggestions(this.lastInterpretation?.original || query).map((item) => ({ query: cleanQuery(item), label: item }));
        const verifiedFallbacks = brainFallbacks.length ? brainFallbacks : [...this.root.querySelectorAll('[data-ai-validate-query]')]
          .map((button) => ({
            query: cleanQuery(button.dataset.aiQuery || button.textContent || ''),
            label: String(button.textContent || '').trim()
          }))
          .filter((item) => item.query && this.validatedQueries.has(item.query.toLowerCase()))
          .filter((item, index, items) => items.findIndex((other) => other.query.toLowerCase() === item.query.toLowerCase()) === index)
          .slice(0, 6);

        this.interpretation.innerHTML = `No instant products matched <strong>${escapeHTML(query)}</strong>`;
        this.results.innerHTML = `
          <div class="framewerk-ai-search__empty">
            <div class="framewerk-ai-search__empty-icon">⌕</div>
            <h3>We couldn't find an instant match</h3>
            <p>${verifiedFallbacks.length ? 'Check the spelling, remove a model or descriptive word, or try one of the verified searches below.' : 'Check the spelling or remove a model or descriptive word.'} You can also use the red button to run a full store search.</p>
            ${verifiedFallbacks.length ? `
              <div class="framewerk-ai-search__corrections">
                ${verifiedFallbacks.map((item) => `<button type="button" class="framewerk-ai-search__correction" data-ai-query="${escapeHTML(item.query)}">${escapeHTML(item.label || item.query)}</button>`).join('')}
              </div>
            ` : ''}
          </div>
        `;
      }

      renderError(query) {
        this.root.classList.remove('is-loading');
        this.main.setAttribute('aria-busy', 'false');
        this.results.hidden = false;
        this.footer.hidden = false;
        this.results.innerHTML = `
          <div class="framewerk-ai-search__error">
            <h3>Search is temporarily unavailable</h3>
            <p>You can still continue to the full search results for “${escapeHTML(query)}”.</p>
          </div>
        `;
      }

      onKeyDown(event) {
        if (event.key === 'Escape' && this.input.value) {
          event.stopPropagation();
          this.reset();
          return;
        }

        const cards = [...this.results.querySelectorAll('[data-ai-product-link]')];
        if (!cards.length) return;

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          cards.forEach((card) => card.classList.remove('is-active'));
          const direction = event.key === 'ArrowDown' ? 1 : -1;
          this.activeIndex = (this.activeIndex + direction + cards.length) % cards.length;
          cards[this.activeIndex].classList.add('is-active');
          cards[this.activeIndex].scrollIntoView({ block: 'nearest' });
        }

        if (event.key === 'Enter' && this.activeIndex >= 0) {
          event.preventDefault();
          cards[this.activeIndex].click();
        }
      }
    }

    const init = () => {
      document.querySelectorAll('[data-framewerk-ai-search]').forEach((root) => {
        if (root.dataset.aiSearchReady === 'true') return;
        root.dataset.aiSearchReady = 'true';
        new FramewerkAISearch(root);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }

    document.addEventListener('shopify:section:load', init);
  })();
